export const FETCH_ORGANIZATIONS_SUCCESS = 'FETCH_ORGANIZATIONS_SUCCESS'
export const FETCH_ORGANIZATIONS_FAILURE = 'FETCH_ORGANIZATIONS_FAILURE'
export const FETCH_ORGANIZATION_SUCCESS = 'FETCH_ORGANIZATION_SUCCESS'
export const FETCH_ORGANIZATION_FAILURE = 'FETCH_ORGANIZATION_FAILURE'
export const CREATE_ORGANIZATION_SUCCESS = 'CREATE_ORGANIZATION_SUCCESS'
export const UPDATE_ORGANIZATION_SUCCESS = 'UPDATE_ORGANIZATION_SUCCESS'
export const DELETE_ORGANIZATION_SUCCESS = 'DELETE_ORGANIZATION_SUCCESS'

import { request } from '../helpers/apiHelper'
import { displayNotification } from './notificationsActions'

const ORGANIZATION_FIELDS = `
  id
  code
  nameLegal
  nameDba
  contactEmail
  contactPhone
  testMode
  addressId
  configurationId
  address { id line1 line2 city state zip }
  configuration { id callCenterName callCenterLogoPath }
`

export const fetchOrganizations = () => (dispatch) => {
  return request(
    `query { organizations { ${ORGANIZATION_FIELDS} } }`,
    {},
    '/admin/graphql'
  )
    .then((response) => {
      dispatch({
        type: FETCH_ORGANIZATIONS_SUCCESS,
        entities: response.data.data.organizations,
      })
    })
    .catch(() => {
      dispatch({ type: FETCH_ORGANIZATIONS_FAILURE })
      dispatch(displayNotification('Failed to fetch Organizations', 'error', 10000))
    })
}

export const fetchOrganization = (id: string) => (dispatch) => {
  return request(
    `query Organization($id: ID!) { organization(id: $id) { ${ORGANIZATION_FIELDS} } }`,
    { id },
    '/admin/graphql'
  )
    .then((response) => {
      const { organization } = response.data.data
      dispatch({ type: FETCH_ORGANIZATION_SUCCESS, entity: organization })
      return Promise.resolve(organization)
    })
    .catch(() => {
      dispatch({ type: FETCH_ORGANIZATION_FAILURE })
      dispatch(displayNotification('Failed to fetch Organization', 'error', 10000))
    })
}

const maybeCreateAddress = (values): Promise<{ id: string } | null> => {
  const { addressId, addressLine1, addressLine2, city, state, zip } = values
  const hasRequired = addressLine1 && city && state && zip

  if (!hasRequired) {
    return addressId ? Promise.resolve({ id: addressId }) : Promise.resolve(null)
  }

  if (addressId) {
    return request(
      `mutation UpdateAddress($input: UpdateAddressInput!) { updateAddress(input: $input) { address { id } } }`,
      {
        input: {
          id: addressId,
          city,
          line1: addressLine1,
          line2: addressLine2,
          state,
          zip,
        },
      },
      '/admin/graphql'
    ).then((res) => {
      const payload = res.data || {}
      if (payload.errors && payload.errors.length) {
        const message = payload.errors.map((e) => e.message).join(', ')
        return Promise.reject(new Error(message))
      }
      const data = payload.data || {}
      return (data.updateAddress && data.updateAddress.address) || { id: addressId }
    })
  }

  return request(
    `mutation CreateAddress($input: CreateAddressInput!) { createAddress(input: $input) { address { id } } }`,
    {
      input: {
        city,
        line1: addressLine1,
        line2: addressLine2,
        state,
        zip,
      },
    },
    '/admin/graphql'
  ).then((res) => {
    const payload = res.data || {}
    if (payload.errors && payload.errors.length) {
      const message = payload.errors.map((e) => e.message).join(', ')
      return Promise.reject(new Error(message))
    }
    const data = payload.data || {}
    return (data.createAddress && data.createAddress.address) || null
  })
}

export const createOrganization = (values) => (dispatch) => {
  return maybeCreateAddress(values)
    .then((addr) => {
      const addressId = addr ? addr.id : values.addressId
      return request(
        `mutation CreateOrganization($input: CreateOrganizationInput!) {
          createOrganization(input: $input) { organization { ${ORGANIZATION_FIELDS} } }
        }`,
        {
          input: {
            code: values.code,
            nameLegal: values.nameLegal,
            nameDba: values.nameDba,
            contactEmail: values.contactEmail,
            contactPhone: values.contactPhone,
            addressId,
            configurationId: values.configurationId,
          },
        },
        '/admin/graphql'
      )
    })
    .then((response) => {
      const { organization } = response.data.data.createOrganization
      dispatch({ type: CREATE_ORGANIZATION_SUCCESS, entity: organization })
      dispatch(displayNotification('Organization created', 'success'))
      return Promise.resolve(organization)
    })
    .catch((error) => {
      dispatch(displayNotification('Failed to create Organization', 'error', 10000))
      return Promise.reject(error)
    })
}

export const updateOrganization = (id: string, values) => (dispatch) => {
  return maybeCreateAddress(values)
    .then((addr) => {
      const addressId = addr ? addr.id : values.addressId
      return request(
        `mutation UpdateOrganization($id: ID!, $code: String, $nameLegal: String, $nameDba: String, $contactEmail: String, $contactPhone: String, $addressId: ID, $configurationId: ID) {
          updateOrganization(id: $id, code: $code, nameLegal: $nameLegal, nameDba: $nameDba, contactEmail: $contactEmail, contactPhone: $contactPhone, addressId: $addressId, configurationId: $configurationId) { ${ORGANIZATION_FIELDS} }
        }`,
        {
          id,
          code: values.code,
          nameLegal: values.nameLegal,
          nameDba: values.nameDba,
          contactEmail: values.contactEmail,
          contactPhone: values.contactPhone,
          addressId,
          configurationId: values.configurationId,
        },
        '/admin/graphql'
      )
    })
    .then((response) => {
      const updated = response.data.data.updateOrganization
      dispatch({ type: UPDATE_ORGANIZATION_SUCCESS, entity: updated })
      dispatch(displayNotification('Organization updated', 'success'))
      return Promise.resolve(updated)
    })
    .catch((error) => {
      dispatch(displayNotification('Failed to update Organization', 'error', 10000))
      return Promise.reject(error)
    })
}

export const deleteOrganization = (id: string) => (dispatch) => {
  return request(
    `mutation DeleteOrganization($input: DeleteOrganizationInput!) {
      deleteOrganization(input: $input) { id }
    }`,
    { input: { id } },
    '/admin/graphql'
  )
    .then(() => {
      dispatch({ type: DELETE_ORGANIZATION_SUCCESS, id })
      dispatch(displayNotification('Organization deleted', 'success'))
    })
    .catch((error) => {
      dispatch(displayNotification('Failed to delete Organization', 'error', 10000))
      return Promise.reject(error)
    })
}
